import React from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { Box, Checkbox, Link, Stack, Typography, Tooltip, useTheme } from '@mui/material'
import { ComponentIcon, DiamondIcon, SquareStackIcon, UngroupIcon } from 'lucide-react'

import { Image, Skeleton } from '@repo/ui'
import { ActionDropdown, type ActionDropdownItem } from 'components/input/ActionDropdown'
import { CERTIFICATE_STATUSES } from 'constants/statuses'
import { useReferrerState } from 'hooks'
import { ROUTES } from 'routes'
import type { CertificateGroupDivisibility, CertificateStatus } from 'types'
// VRG-777 — `products.images` holds stills AND video in one ordered list, so `images[0]`
// is no longer necessarily a still. `getProductThumbnail` returns the first non-video
// entry; shared in @repo/types so this branch is not re-written at each of the ~20 sites
// that read the column (§36).
import { getProductThumbnail } from '@repo/types'

// VRG-231 review item 3 — no dedicated "short id" field exists for certificates
// anywhere in the platform (grepped CertificateCell, the Certificates list
// columns, and the Certificate type — only the raw UUID `id` and the physical
// tag's `e_tag_id`/`tag_id`, neither of which is a human-readable slug). The
// Figma still's "vt-lv-1965sr03" is mock placeholder copy with no backing
// field. Rather than invent a bespoke format, this reuses the app's existing
// "short id" idiom — an 8-char prefix of the UUID — already used for order ids
// in Invoices.tsx / InternalInvoicesPage.tsx (`String(row.orderId).slice(0, 8)`).
// VRG-231 review item 2 — exported (was a private const) so
// CreateCertificateGroupModal can show the same short-id format on the
// parent-product row's sub-line, disambiguating which certificate seeded the
// parent's product.
export const shortCertificateId = (id: string) => id.slice(0, 8)
const shortId = shortCertificateId

// A certificate's role within its group — decides which glyph renders (see
// `CertificateGroupRoleIcon` below). `undefined` renders no glyph at all (the
// certificate isn't part of any group).
export type CertificateGroupRole = 'parent' | 'child'

// The minimal shape this preview needs — a full `CertificateAdmin` satisfies it.
export interface CertificateGroupMemberLike {
  id: string
  status?: CertificateStatus
  /** Denormalized onto every member of the group (VRG-234/235) — drives the
   *  divisible/indivisible tooltip copy on the role glyph (VRG-231 review item 6). */
  group_divisibility?: CertificateGroupDivisibility | null
  product?: {
    title?: string
    sku?: string
    images?: string[]
  } | null
}

/**
 * The glyph shown beside a group member's name (and, via `role` alone with no
 * card chrome, beside the page title in `Header`'s `leftComponents` — see
 * CertificatePage.tsx).
 *
 * SHAPE — VRG-231 review round 2 item 5 — when `divisibility` is known
 * (denormalized onto every member of a group, parent and children alike), the
 * shape names the GROUP's divisibility rather than this member's role:
 * `UngroupIcon` for divisible (matches `DivisibilityOptionPicker`'s own choice
 * — same glyph, same meaning, everywhere divisibility is shown, per §36), and
 * `SquareStackIcon` for indivisible. This supersedes the previous role-only shape
 * (`ComponentIcon` for a parent, `DiamondIcon` for a child) as the PRIMARY
 * signal, but that fallback is kept for the rare caller that hasn't resolved
 * divisibility yet — role is otherwise already legible from context wherever
 * this glyph appears (the list's own ↳ child indent, the "N items" badge, the
 * Children/Parent/Related section headings), so nothing is lost by not
 * repeating it in the glyph's shape once divisibility is known.
 *
 * VRG-231 review round 3 — `LayersIcon` became `SquareStackIcon` because
 * `LayersIcon` was ALSO the "Create group" action's glyph. The pair now shares
 * one primitive at two separations (Ungroup's rounded squares carry a gap;
 * SquareStack's overlap into one stack), so the two values read as one axis
 * rather than two unrelated icons. That matters more here than in the picker:
 * see the COLOR note below — on this badge, shape is the ONLY channel carrying
 * divisibility, so the two shapes have to differ coarsely enough to survive
 * 20px. Full reasoning, including the second collision this also resolved,
 * lives in DivisibilityOptionPicker's own header comment.
 *
 * COLOR — unchanged, still the certificate's STATUS color (matching the
 * "Active" badge next to it) — a status is a fact about this ONE member,
 * where divisibility is a fact about the GROUP, and conflating the two into
 * one color would lose the status signal entirely. Divisibility's own color
 * (green/orange, matching `DivisibilityOptionPicker` and the Figma list
 * stills) lives on the SHAPE choice instead — shape and color now carry two
 * independent facts, not one fact through two redundant channels.
 *
 * TOOLTIP — VRG-231 review round 2 item 6 — the tooltip earns its place only
 * by saying WHICH KIND of group this is; the layout already carries "this is
 * a group" (the shape, the indent, the badge). Both branches (parent AND
 * child) now name the divisibility — a child on an indivisible group used to
 * get the same generic "Part of a certificate group" copy as a divisible
 * child, which told the reader nothing the shape/indent hadn't already.
 * `divisibility` is optional so existing call sites that don't have it yet
 * still render (falling back to generic copy) rather than crashing.
 */
export const CertificateGroupRoleIcon = ({
  role,
  status,
  divisibility,
  size = 20,
}: {
  role: CertificateGroupRole
  status?: CertificateStatus
  divisibility?: CertificateGroupDivisibility | null
  size?: number
}) => {
  const theme = useTheme()
  // Not every CertificateStatus key (e.g. transfer_initiated) has an entry in
  // CERTIFICATE_STATUSES — same permissive lookup StatusChip itself uses.
  const statuses = CERTIFICATE_STATUSES as Record<string, { color: string }>
  const color = (status && statuses[status]?.color) || theme.palette.grey[400]
  const Icon =
    divisibility === 'divisible'
      ? UngroupIcon
      : divisibility === 'indivisible'
        ? SquareStackIcon
        : role === 'parent'
          ? ComponentIcon
          : DiamondIcon
  const label =
    divisibility === 'divisible'
      ? 'Part of a divisible group. Components can be sold separately.'
      : divisibility === 'indivisible'
        ? 'Part of an indivisible group. Only sold as a unit.'
        : role === 'parent'
          ? 'Group parent — made up of multiple components'
          : 'Part of a certificate group'

  return (
    <Tooltip title={label}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
        <Icon size={size} strokeWidth={1.5} color={color} />
      </Box>
    </Tooltip>
  )
}

export interface CertificateGroupMemberPreviewProps {
  certificate: CertificateGroupMemberLike
  /** Which glyph to render beside the name. Omit to render none. */
  role?: CertificateGroupRole
  /** Row kebab (Preview / Details / Replace tag / Transfer / Split from group / Delete — full certificate action set, VRG-231 review item 5). Omit to hide the kebab. */
  actionItems?: ActionDropdownItem[]
  size?: number
  /**
   * VRG-337 — when provided, the row becomes a SELECTABLE checkbox row (used by
   * the "Group certificates" member picker, per working-conventions §28's
   * AssignUsersModal pattern) instead of a navigation link: a leading checkbox
   * is rendered and the whole row toggles selection on click rather than
   * navigating to the certificate's own page. `actionItems` is ignored in this
   * mode (a picker row has no per-row actions).
   */
  selected?: boolean
  onToggleSelected?: () => void
  /**
   * This row exists but cannot be interacted with. What that means depends on
   * the mode, because the two modes have different interactions to suppress:
   *
   * - **Picker mode** (`onToggleSelected` given) — the row exists but cannot be
   *   CHOSEN: a real `disabled` on the real checkbox (see below).
   * - **Read-only mode** (no `onToggleSelected`) — the row exists but does not
   *   NAVIGATE. It renders as plain content instead of a `Link`, and drops the
   *   action-row hover/focus treatment (§34), which would otherwise promise a
   *   click target that does nothing.
   *
   * The read-only half was added for the activation confirm modal (VRG-231),
   * which lists the other group members a cascade will also activate. Those
   * rows are EVIDENCE, not destinations: a link inside a confirmation dialog
   * navigates away mid-decision and abandons the action the user opened the
   * modal to take. Extending this prop rather than adding a `readOnly` sibling
   * keeps one answer to "can this row be interacted with" (§47 — improve the
   * shared component rather than growing a parallel one).
   *
   * VRG-231 review round 3. Added because the member picker was approximating it
   * from the OUTSIDE (a wrapper with `opacity` + `pointerEvents: 'none'` + a
   * `.MuiCheckbox-root` colour override), which is the shape of a workaround: it
   * blocks the pointer but the checkbox is still `enabled` in the accessibility
   * tree, so a screen reader offers a control that does nothing. Owning it here
   * puts the real `disabled` on the real input, and any second caller that needs
   * an unselectable row gets it without re-deriving the workaround (§36 — the
   * second hand-roll is the trigger, and this is the point before it).
   *
   * Deliberately NOT paired with a `reason` prop: WHY a row is unavailable is the
   * caller's domain knowledge, not this component's, and the picker already
   * renders its own reason line from `getIneligibleReason`.
   */
  disabled?: boolean
}

/**
 * Shared certificate-group-member row: thumbnail + product name + group-role glyph +
 * certificate id + an optional kebab. Single source for the "Children" (on a parent),
 * "Parent", and "Related" (siblings, on a child) sections on CertificatePage — those
 * three lists render the SAME entity three different places, so per working-conventions
 * §36 this is built once rather than hand-rolled per section.
 *
 * The whole row (minus the kebab) is a Link to the referenced certificate's own page,
 * carrying `useReferrerState()` so that page can render "← Back to <this certificate>"
 * (§42). Hover/focus uses the action-row treatment (§34) — clicking always navigates
 * away, there's no persistent "selected" state to preserve.
 */
export const CertificateGroupMemberPreview = ({
  certificate,
  role,
  actionItems,
  // VRG-231 review item 4 — bumped from 48 to 64 to match the larger, more
  // prominent card the Figma stills show; the row's own padding follows suit
  // (p: 3 -> p: 4) so the thumbnail doesn't feel cramped against the border.
  size = 64,
  selected,
  onToggleSelected,
  disabled = false,
}: CertificateGroupMemberPreviewProps) => {
  const theme = useTheme()
  // Detail → detail link: the derived label would be "Certificates" (the parent
  // LIST), but `backTo` points at a single record — so pass an explicit override,
  // per useReferrerState's documented convention for this exact case.
  const referrer = useReferrerState('Certificate')
  const visibleItems = (actionItems ?? []).filter(item => item.visible !== false)
  const isSelectable = typeof onToggleSelected === 'function'
  // Not selectable AND disabled = the read-only third mode: neither a checkbox
  // row nor a link, just the row's content. See `disabled`'s prop note.
  const isReadOnly = !isSelectable && disabled

  const content = (
    <>
      <Image
        src={getProductThumbnail(certificate.product?.images) ?? ''}
        alt={certificate.product?.title}
        fit="cover"
        sx={{
          width: size,
          height: size,
          borderRadius: 2,
          border: `1px solid ${theme.palette.grey[200]}`,
          flexShrink: 0,
        }}
      />
      {/* VRG-231 review item 3 — title alone didn't identify the product (many
          certificates can share a title); the SKU is added as a sub-line,
          stacked under the title+role-icon row. The right-aligned short id
          (below) is untouched — it identifies the CERTIFICATE, this identifies
          the PRODUCT. */}
      <Stack spacing={1} minWidth={0} sx={{ flex: 1 }}>
        <Stack direction="row" spacing={2} alignItems="center" minWidth={0}>
          <Typography variant="body2" noWrap>
            {certificate.product?.title ?? 'Certificate'}
          </Typography>
          {role && (
            <CertificateGroupRoleIcon
              role={role}
              status={certificate.status}
              divisibility={certificate.group_divisibility}
            />
          )}
        </Stack>
        {certificate.product?.sku && (
          <Typography variant="body3" color="text.secondary" noWrap>
            {certificate.product.sku}
          </Typography>
        )}
      </Stack>
      <Tooltip title={certificate.id}>
        <Typography variant="body3" color="text.secondary" noWrap sx={{ flexShrink: 0, cursor: 'default' }}>
          {shortId(certificate.id)}
        </Typography>
      </Tooltip>
    </>
  )

  const rowContentSx = {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    textDecoration: 'none',
    color: 'inherit',
  } as const

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        p: 4,
        borderRadius: 2,
        border: `1px solid ${theme.palette.primary[100]}`,
        backgroundColor: theme.palette.background.paper,
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
        // Read-only rows get no hover/focus affordance — see `disabled`'s note.
        // A brand border + ring on a row that cannot be clicked reads as a
        // target, which is the one thing a piece of evidence must not do.
        ...(isReadOnly
          ? {}
          : {
              '&:hover, &:focus-within': {
                borderColor: theme.palette.brand[600],
                boxShadow: `0 0 0 3px ${theme.palette.brand['600-6%']}`,
              },
            }),
      }}
    >
      {isSelectable && (
        <Checkbox
          checked={Boolean(selected)}
          onChange={onToggleSelected}
          onClick={e => e.stopPropagation()}
          size="small"
          // VRG-231 review round 3 — a REAL `disabled`, not a tint. The picker
          // previously faked this from its wrapper (opacity + pointerEvents:none +
          // a colour override), which stops the click but leaves the checkbox
          // announced as enabled to a screen reader — the wrapper's `aria-disabled`
          // does not reach the input. `disabled` is the only thing that does.
          disabled={disabled}
          sx={{ flexShrink: 0 }}
        />
      )}
      {isSelectable ? (
        // VRG-337 — picker mode: the row toggles selection instead of
        // navigating away (there's no certificate page to link to while
        // picking members for a group that doesn't exist yet).
        <Box
          onClick={disabled ? undefined : onToggleSelected}
          sx={{ ...rowContentSx, cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          {content}
        </Box>
      ) : isReadOnly ? (
        // VRG-231 — read-only mode: content only. No Link, because this row is
        // evidence inside a confirmation dialog and navigating away mid-decision
        // abandons the action the modal exists to confirm.
        <Box sx={rowContentSx}>{content}</Box>
      ) : (
        <Link component={RouterLink} to={ROUTES.CERTIFICATE(certificate.id)} state={referrer} sx={rowContentSx}>
          {content}
        </Link>
      )}
      {!isSelectable && visibleItems.length > 0 && <ActionDropdown items={visibleItems} />}
    </Box>
  )
}

/**
 * Loading placeholder — mirrors the real row's footprint exactly (same border/
 * padding/gap, a circular thumbnail-sized skeleton, a title-width bar, an id-width
 * bar) so there's zero layout shift once the group query resolves. See §19.
 */
export const CertificateGroupMemberPreviewSkeleton = ({ size = 64 }: { size?: number }) => {
  const theme = useTheme()
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        p: 4,
        borderRadius: 2,
        border: `1px solid ${theme.palette.primary[100]}`,
      }}
    >
      {/* "rounded" (not "circular") — the real thumbnail is a rounded square
          (Image, borderRadius: 2), so the skeleton matches its actual footprint. */}
      <Skeleton variant="rounded" width={size} height={size} />
      <Stack spacing={1} sx={{ flex: 1 }}>
        <Skeleton variant="rounded" width="40%" height={14} />
      </Stack>
      <Skeleton variant="rounded" width={80} height={14} />
    </Box>
  )
}

// VRG-231 review item 8 — extracted from CertificatePage's Product.tsx (originally
// review item 4's "Children"/"Parent" tree) so SplitGroupModal can render the SAME
// tree/indent graphic rather than rebuilding a second one (§36: one presentational
// component per repeated UI unit). Reproduces the Figma stills' indented/nested
// look: each member row sits inset under its section heading, connected by an "L"
// tree line (a vertical spine running past every row but the last, plus a
// horizontal stub into each card). Pure CSS, no JS measurement — the vertical spine
// of a non-last row runs its FULL height (top:0 to bottom:0) so it meets the next
// row's spine and reads as one continuous line; the last row's spine stops at its
// own midpoint (bottom:50%) so nothing dangles below the final card.
const GROUP_MEMBER_LIST_INDENT = 28
// VRG-231 review round 3 — the horizontal stub was 14px and read as a nub. Both
// constants move together: the spine sits at -20 and the stub runs the full 20px
// back to the card's edge, so the run is 6px longer without overlapping the card.
const GROUP_MEMBER_LIST_LINE_X = -20
const GROUP_MEMBER_LIST_STUB_WIDTH = 20

export const GroupMemberList = ({
  children,
  connector = true,
}: {
  /**
   * Widened from `ReactNode[]` (VRG-231). The array type was wrong on both
   * sides: `React.Children.toArray` — the only thing this component does with
   * children — is typed `ReactNode | ReactNode[]` and handles a single node
   * fine, while the Parent and Related sections each pass ONE conditional
   * expression, which is a single child however many branches it has. That
   * mismatch was two live TS2745 errors at those two call sites.
   */
  children: React.ReactNode
  /**
   * Draw the "L" tree line into each row. Default `true`.
   *
   * VRG-231 review round 3 — pass `false` for a section whose members are NOT
   * BELOW the current certificate in the group's hierarchy. The connector is a
   * containment graphic: a vertical spine descending from the section heading
   * into each row says "these hang off that". That is true of **Children** and
   * false of the other two sections, where it actively misinforms:
   *
   * - **Parent** — the parent is ABOVE this certificate. A line descending into
   *   it draws the hierarchy upside down.
   * - **Related** — siblings are PEERS. There is no containment to draw at all,
   *   and the line implies the current certificate owns them.
   *
   * The INDENT goes with the line. An earlier version kept the 28px inset even
   * with `connector={false}`, justified as keeping Children, Parent and Related
   * aligned on one left edge — and that justification was simply wrong: the
   * sections are mutually exclusive. `Product.tsx` renders Children only when
   * `groupRole === 'parent'` and Parent/Related only when it is `'child'`, so
   * there has never been a page where two of them could misalign. What the inset
   * actually produced was a card offset from its own heading with nothing to
   * explain the offset, which is exactly how it reads. An indent is the space a
   * connector occupies; with no connector it is unexplained space.
   */
  connector?: boolean
}) => {
  const theme = useTheme()
  const items = React.Children.toArray(children)
  return (
    <Stack spacing={3} sx={{ pl: connector ? `${GROUP_MEMBER_LIST_INDENT}px` : 0 }}>
      {items.map((child, index) => {
        const isLast = index === items.length - 1
        return (
          <Box
            key={index}
            sx={
              connector
                ? {
                    position: 'relative',
                    '&:before': {
                      content: '""',
                      position: 'absolute',
                      left: GROUP_MEMBER_LIST_LINE_X,
                      top: 0,
                      bottom: isLast ? '50%' : 0,
                      width: '1px',
                      backgroundColor: theme.palette.grey[300],
                    },
                    '&:after': {
                      content: '""',
                      position: 'absolute',
                      left: GROUP_MEMBER_LIST_LINE_X,
                      top: '50%',
                      width: GROUP_MEMBER_LIST_STUB_WIDTH,
                      height: '1px',
                      backgroundColor: theme.palette.grey[300],
                    },
                  }
                : { position: 'relative' }
            }
          >
            {child}
          </Box>
        )
      })}
    </Stack>
  )
}

export default CertificateGroupMemberPreview
